import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { GraduationCap, Trophy, Flame, BookOpen, ArrowRight, Play } from 'lucide-react'
import { LearningProgress as LearningProgressType } from '../../types/dashboard'
import { UserLearningProgress, LearningAchievement } from '../../types/learning'

interface LearningProgressProps {
  progress: LearningProgressType | UserLearningProgress | null
  achievements?: LearningAchievement[]
  loading?: boolean
}

const LearningProgress: React.FC<LearningProgressProps> = ({ 
  progress, 
  achievements = [], 
  loading = false 
}) => {
  // Handle both old dashboard type and new learning type
  const isLearningProgress = progress && 'total_xp_earned' in progress && 'level' in progress

  if (loading || !progress) {
    return (
      <motion.div
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.2 }}
        className="luxury-card h-full"
      >
        <div className="flex items-center justify-center h-64">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            className="w-8 h-8 border-2 border-primary-500/20 border-t-primary-500 rounded-full"
          />
        </div>
      </motion.div>
    )
  }

  const getProgressData = () => {
    if (isLearningProgress) {
      const learningProgress = progress as UserLearningProgress
      return {
        level: learningProgress.level,
        experience: learningProgress.total_xp_earned,
        experienceToNext: learningProgress.xp_to_next_level,
        streak: learningProgress.current_streak,
        nextLesson: learningProgress.continue_learning_video?.title || learningProgress.next_recommended_video?.title || 'Explore Learning Center',
        achievements: achievements.slice(0, 2).map(achievement => ({
          id: achievement.id,
          name: achievement.name,
          description: achievement.description || '',
          icon: achievement.icon || '🏆'
        }))
      }
    } else {
      return progress as LearningProgressType
    }
  }

  const progressData = getProgressData()
  const progressPercentage = progressData.experienceToNext > 0 
    ? ((progressData.experience % 1000) / 1000) * 100 
    : 100

  const handleContinueLearning = () => {
    if (isLearningProgress) {
      const learningProgress = progress as UserLearningProgress
      if (learningProgress.continue_learning_video) {
        return `/learn/video/${learningProgress.continue_learning_video.id}`
      } else if (learningProgress.next_recommended_video) {
        return `/learn/video/${learningProgress.next_recommended_video.id}`
      }
    }
    return '/learn'
  }

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
      className="luxury-card h-full"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-background-100">
          Learning Progress
        </h3>
        <div className="flex items-center space-x-2">
          <Flame className="w-4 h-4 text-orange-500" />
          <span className="text-sm font-semibold text-orange-500">
            {progressData.streak} day streak
          </span>
        </div>
      </div>

      {/* Level and XP */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <GraduationCap className="w-5 h-5 text-primary-500" />
            <span className="text-lg font-semibold text-background-100">
              Level {progressData.level}
            </span>
          </div>
          <span className="text-sm text-background-400">
            {progressData.experience} XP
          </span>
        </div>
        
        {/* XP Progress Bar */}
        <div className="w-full bg-background-700 rounded-full h-3 mb-2">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 1, delay: 0.5 }}
            className="bg-gradient-to-r from-primary-500 to-primary-400 h-3 rounded-full"
          />
        </div>
        <div className="text-xs text-background-500">
          {progressData.experienceToNext > 0 
            ? `${progressData.experienceToNext} XP to next level`
            : 'Max level reached!'
          }
        </div>
      </div>

      {/* Learning Stats */}
      {isLearningProgress && (
        <div className="mb-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-background-800 rounded-lg">
              <div className="text-lg font-bold text-background-100">
                {(progress as UserLearningProgress).total_videos_watched}
              </div>
              <div className="text-xs text-background-500">
                Videos
              </div>
            </div>
            <div className="text-center p-3 bg-background-800 rounded-lg">
              <div className="text-lg font-bold text-background-100">
                {(progress as UserLearningProgress).achievements_earned}
              </div>
              <div className="text-xs text-background-500">
                Achievements
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Achievements */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-background-300 mb-3">
          Recent Achievements
        </h4>
        <div className="space-y-2">
          {progressData.achievements.length > 0 ? (
            progressData.achievements.slice(0, 2).map((achievement, index) => (
              <motion.div
                key={achievement.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="flex items-center space-x-3 p-2 bg-background-800 rounded-lg"
              >
                <div className="text-2xl">{achievement.icon}</div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-background-100">
                    {achievement.name}
                  </div>
                  <div className="text-xs text-background-400">
                    {achievement.description}
                  </div>
                </div>
                <Trophy className="w-4 h-4 text-yellow-500" />
              </motion.div>
            ))
          ) : (
            <div className="text-center p-4 text-background-500">
              <div className="text-sm">No achievements yet</div>
              <div className="text-xs">Start learning to earn your first!</div>
            </div>
          )}
        </div>
      </div>

      {/* Next Lesson */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-background-300 mb-3">
          Continue Learning
        </h4>
        <div className="p-3 bg-gradient-to-r from-primary-500/10 to-primary-300/10 rounded-xl">
          <div className="flex items-center space-x-3">
            <BookOpen className="w-5 h-5 text-primary-500" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-background-100">
                {progressData.nextLesson}
              </div>
              <div className="text-xs text-background-400">
                Ready to start
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Button */}
      <Link to={handleContinueLearning()}>
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full luxury-button primary group"
        >
          {isLearningProgress && (progress as UserLearningProgress).continue_learning_video ? (
            <>
              <Play className="w-4 h-4 mr-2" />
              Resume Learning
            </>
          ) : (
            <>
              Continue Learning
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </motion.div>
      </Link>
    </motion.div>
  )
}

export default LearningProgress


